import AutoTableLayout, { ITableLayoutItem } from '@Common/Controls/AutoTableLayout';
import { FilterField } from '@Common/Controls/FilterField';
import { dimensionFilterValue, filterValue } from '@Common/Entities/FilterEntities';
import { EnumValue, FilterType, TypeKind } from '@Common/Enums';
import { toMap } from '@Common/Helpers/CollectionUtils';
import documentHelper from '@Common/Helpers/DocumentHelper';
import { DimensionFilterValues, FieldCondition } from '@InstanceFactory';

import { filterHelper } from '@View/Helpers/FilterHelper';
import formatHelper from '@Common/Helpers/FormatHelper';
import { MulticastDelegate } from '@Common/Core/MulticastDelegate';

export interface ILayoutControlParam {
    css?: object;
    className?: string;
    width?: string;
    height?: string;
    margin: number;
    numberOfColumns: number;
    items: LayoutControlField[];
    labelWidth: number;
    fieldWidth: number;
    operatorWidth?: number;
    labelStyleName: string;
    showOperators: boolean;
    canEdit: boolean;
}

export type LayoutControlField = documentHelper.IRecordFieldItem & { defaultFilterType?: string };

type FilterTypeItem = EnumValue<FilterType>;

export class LayoutControl {
    private static readonly excludeTypes = {
        [TypeKind.bool]: [FilterType.and, FilterType.or],
        all: [FilterType.isNotNull, FilterType.isNull]
    };
    private readonly getFilterForFilterTypeItems = _.memoize((fieldType: TypeKind) => {
        const excluded = new Set([...(LayoutControl.excludeTypes[fieldType] || []), ...LayoutControl.excludeTypes.all]);

        return ft => !excluded.has(ft.id);
    });

    private table: JQuery;
    private fields: FilterField[] = [];

    public onEnterPressed = MulticastDelegate.create<ICallback<JQuery.Event>>();
    public onDataRequested = MulticastDelegate.create<(dimensionId: string, startResultIndex: number, pageSize: number) => any>();

    constructor(private readonly param: ILayoutControlParam) {
        this.param = _.defaults(param, {
            css: null,
            className: '',
            width: 'auto',
            height: 'auto',
            margin: 0,
            numberOfColumns: 1,
            labelWidth: 100,
            fieldWidth: 100,
            operatorWidth: 100,
            labelStyleName: '',
            showOperators: false,
            canEdit: false,
            items: []
        });
        this.init();
    }

    private static getDefaultFilterType(typeKind: TypeKind): FilterTypeItem {
        switch (typeKind) {
            case TypeKind.string:
                return FilterType.getValue(FilterType.includes);

            case TypeKind.int:
            case TypeKind.float:
            case TypeKind.guid:
                return FilterType.getValue(FilterType.equal);

            case TypeKind.dateTime:
                return FilterType.getValue(FilterType.range);
        }

        return FilterType.getValue(FilterType.equal);
    }

    public init(): void {
        this.table = $('<table><thead></thead><tbody></tbody></table>');
        this.table.css({
            padding: this.param.margin,
            width: '100%'
        });

        const layoutItems = this.param.items.map(i => _.defaults(i, { rowsCount: 1 }));
        const layout = new AutoTableLayout(layoutItems, this.param.numberOfColumns);
        const rows = layout.doLayout();
        const columnCount = Math.max(...rows.map(r => r.length));
        const columnSize = Math.round(100 / columnCount) + '%';
        for (const row of rows) {
            const tableRow = $('<tr>').appendTo(this.table);
            for (const cell of row) {
                if (cell) {
                    const field = this.createField(cell);
                    const td = $('<td>').attr('width', columnSize).appendTo(tableRow);
                    if (cell.rowsCount > 1) {
                        td.attr('rowspan', cell.rowsCount);
                    }
                    field.getControl().appendTo(td);
                    this.fields.push(field);
                }
            }
            if (tableRow.children('td').length === 0) {
                $('<td>').appendTo(tableRow);
            }
        }
    }

    public clear(): void {
        for (const field of this.fields) {
            field.clear();
        }
    }

    public getDimensionFilterConditions(): DimensionFilterValues[] {
        return _(this.fields)
            .map(i => {
                const data = i.getValue();
                data.values = _.map(data.values, n => filterValue(n));
                return dimensionFilterValue(data.fieldPath, data.values, data.operatorType);
            })
            .filter(i => i !== null && _.some(i.values, n => n.id))
            .value();
    }

    public setValue(values: FieldCondition[]): void {
        const valTree = toMap(values, v => [v.fieldPath]);
        for (const field of this.fields || []) {
            const value = field.getValue();
            const newValue = valTree.get(value.fieldPath);
            if (newValue) {
                field.setValue(newValue);
            } else {
                field.clear();
            }
        }

        this.adjust();
    }

    public adjust(): void {
        $('textarea', this.table).each((i, e) => {
            const area = $(e);
            const td = area.closest('td');
            if (_.toNumber(td.attr('rowspan')) > 1) {
                const padding = _(['padding-top', 'padding-bottom'])
                    .map(prop => _.toNumber(td.css(prop)))
                    .sum();
                area.outerHeight(td.innerHeight() - padding);
            }
        });
    }

    public getControl(): JQuery {
        return this.table;
    }

    private createField(item: LayoutControlField & ITableLayoutItem): FilterField {
        const filterTypes = filterHelper.getFilterTypes(item.fieldType).filter(this.getFilterForFilterTypeItems(item.fieldType));

        const dateTimeFormat = formatHelper.getDateTimeFormat(item.formatName);
        const filterField = new FilterField({
            fieldType: item.fieldType,
            label: {
                width: this.param.labelWidth,
                content: item.name,
                styleName: this.param.labelStyleName
            },
            operator: {
                items: filterTypes,
                width: this.param.operatorWidth,
                show: this.param.showOperators
            },
            input: {
                width: this.param.fieldWidth,
                canEdit: this.param.canEdit,
                showTime: dateTimeFormat.withTime,
                format: dateTimeFormat.format,
                rowsCount: item.rowsCount
            }
        });

        const defaultFilterType =
            filterTypes.firstOrNull(ev => ev.id === item.defaultFilterType) || LayoutControl.getDefaultFilterType(item.fieldType);

        filterField.setValue(
            FieldCondition.create({
                fieldPath: item.id,
                operatorType: defaultFilterType.id,
                values: ['', '']
            })
        );
        filterField.onEnterPressed(e => {
            this.onEnterPressed(e);
        });
        filterField.onDataRequested((startResultIndex, pageSize) => this.onDataRequested(item.id, startResultIndex, pageSize));

        return filterField;
    }
}
